import { HttpClient } from "@src/utils/httpClient";

interface SharePointItemViewModel {
  name: string;
  path: string;
  item_type: "file" | "folder";
  size: number | null;
  modified: string | null;
  item_id: string;
}

export interface SharePointFolderViewModel {
  site: string;
  library: string;
  root_folder: string;
  path: string;
  items: SharePointItemViewModel[];
}

export interface SharePointSelection {
  path: string;
  name: string;
  /** Omitted to import every PDF in the folder and its subfolders. */
  filePaths?: string[];
}

interface SharePointImportOptions {
  doctypeCode: string;
  pageNumbers?: string;
  instructions?: string;
  mappingEnabled?: boolean;
}

interface SharePointImportResponse {
  process_id: string;
  status: string;
}

const BASE_URL = import.meta.env.VITE_EXTRACTION_API_BASE_URL;

if (!BASE_URL) {
  throw new Error(
    "Missing required environment variable: VITE_EXTRACTION_API_BASE_URL.",
  );
}

class SharePointApi {
  private readonly httpClient = new HttpClient(BASE_URL);

  public async listItems(doctypeCode: string, path = ""): Promise<SharePointFolderViewModel> {
    const query = new URLSearchParams({ path, doctype_code: doctypeCode });
    return this.httpClient.makeAuthenticatedRequest<SharePointFolderViewModel>(
      `/sharepoint/items?${query.toString()}`,
      { method: "GET" },
    );
  }

  public async importFolder(
    folderPath: string,
    options: SharePointImportOptions,
    filePaths?: string[],
  ): Promise<SharePointImportResponse> {
    return this.httpClient.makeAuthenticatedRequest<SharePointImportResponse>(
      "/sharepoint/imports",
      {
        method: "POST",
        body: JSON.stringify({
          folder_path: folderPath,
          file_paths: filePaths,
          doctype_code: options.doctypeCode,
          page_numbers: options.pageNumbers,
          instructions: options.instructions,
          mapping_enabled: options.mappingEnabled ?? false,
        }),
      },
    );
  }
}

export const sharePointApi = new SharePointApi();
